/* eslint-disable @typescript-eslint/no-var-requires */
import { motion } from 'framer-motion';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Location, useNavigate } from 'react-router-dom';
import { GENERIC_ERROR_DATA } from '../config/errors';
import DefaultLayout from '../layout/DefaultLayout';
import { isFleetcare, isMindy, isOms } from '../utils';

type LocationState = Location & {
    state: {
        title: string;
        body: string;
        img: string;
        renderCta: boolean;
    };
};

const GenericError: React.FC = () => {
    let title = GENERIC_ERROR_DATA.title;
    let body = GENERIC_ERROR_DATA.body;
    let img = GENERIC_ERROR_DATA.img;
    let renderCta = true;

    const navigate = useNavigate();
    const location = useLocation() as LocationState;
    const { t } = useTranslation();
    const { state, search } = location;

    const queryParams = new URLSearchParams(search);

    title = queryParams.get('title') ?? state?.title ?? title;
    body = queryParams.get('body') ?? state?.body ?? body;
    img = queryParams.get('img') ?? state?.img ?? img;
    renderCta = queryParams.get('renderCta') === 'true' || state?.renderCta || renderCta;

    function handleNavigate() {
        const token = localStorage.getItem('token');
        if (token) {
            localStorage.removeItem('token');
        }

        // Keeps the reference of the redirect url after error page
        // This is especially important for saas platform, where exist many apps
        // such us saas, mindy and merchants
        const redirect = queryParams.get('redirect') ?? '';
        return navigate({ pathname: '/login', search: redirect ? `redirect=${redirect}` : undefined });
    }

    if (isMindy || isFleetcare || isOms) {
        return (
            <DefaultLayout>
                <div className="w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="space-y-5"
                    >
                        <h2 className="text-2xl font-poppins font-semibold text-white" data-test="error-title">
                            {t(title)}
                        </h2>
                        <div className="border-t border-gray-700" />
                        <div className="border border-red-500 rounded-lg p-4 space-y-1">
                            <p className="text-sm font-roboto text-red-400">{t(body)}</p>
                        </div>
                        <div className="border-t border-gray-700" />
                        {renderCta && (
                            <button
                                type="button"
                                onClick={() => handleNavigate()}
                                className="w-full bg-primary-500 hover:bg-primary-400 text-white font-poppins font-semibold py-3 rounded-lg transition-colors"
                            >
                                {t('login.goBack')}
                            </button>
                        )}
                    </motion.div>
                </div>
            </DefaultLayout>
        );
    }

    return (
        <DefaultLayout>
            <div className="bg-white xs:w-1/2 xs:shadow-xl shadow-slate-500/50 px-14 py-10 rounded-md font-['Roboto'] flex flex-col justify-center items-start max-w-[480px]">
                <img className="self-center mb-3" src={require(`../assets/${img}.svg`).default} alt="" />
                <h3 data-test="error-title" className="text-3xl font-bold mb-3">
                    {t(title)}
                </h3>
                <p className="text-base w-full mb-3">{t(body)}</p>
                {renderCta && (
                    <button
                        className="w-full h-[50px] bg-blue-800 disabled:bg-blue-200 rounded-md text-white font-['Poppins']"
                        type="button"
                        color="blue"
                        onClick={() => handleNavigate()}
                    >
                        {t('login.goBack')}
                    </button>
                )}
            </div>
        </DefaultLayout>
    );
};

export default GenericError;
